import React, { useEffect, useRef, useState } from 'react';
import { Feather } from '@expo/vector-icons';
import { Platform, View } from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../../constants/Colors';
import { useUserContext } from '../../../context/UserContext';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import { FormPhoneNumber } from './FormPhoneNumberPlugin';
import { FormTextField } from './FormTextFieldPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import Spacer from '../../../components/Spacer';
import { Caption } from '../../../components/Typography';

type ContactCollectionMode = 'Email' | 'SMS' | 'Both' | 'Either';

interface PediatricContactCollectionConfig {
  key?: string;
  contactCollection?: ContactCollectionMode;
  emailHeader?: string;
  phoneHeader?: string;
}

const DEFAULT_EMAIL_HEADER = "Child's Email";
const DEFAULT_PHONE_HEADER = "Child's Phone Number";

const allowsEmail = (mode?: ContactCollectionMode) =>
  mode === 'Email' || mode === 'Both' || mode === 'Either';

const allowsSms = (mode?: ContactCollectionMode) =>
  mode === 'SMS' || mode === 'Both' || mode === 'Either';

const notificationRequirementMet = (
  mode: ContactCollectionMode,
  emailUsable: boolean,
  notifyForEmail: unknown,
  phoneNumber: unknown,
  notifyForText: unknown
) => {
  if (mode === 'Email') return Boolean(notifyForEmail);
  if (mode === 'SMS') return Boolean(notifyForText);
  if (mode === 'Both') return Boolean(notifyForEmail || notifyForText);
  return Boolean(
    (notifyForEmail && emailUsable) || (notifyForText && phoneNumber)
  );
};

const parentDataSource = (element: {
  config?: PediatricContactCollectionConfig;
}): 'email' | 'phoneNumber' =>
  allowsEmail(element.config?.contactCollection || 'Both')
    ? 'email'
    : 'phoneNumber';

const firstStudyWithPediatricFlag = (
  ...studies: Array<{ isPediatric?: boolean } | null | undefined>
) =>
  studies.find(
    study => study != null && typeof study.isPediatric === 'boolean'
  ) ?? studies.find(study => study != null);

const emailRegExp =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

const isValidEmail = (email?: string) =>
  Boolean(email && emailRegExp.test(email.trim()));

const instanceKeys = (element: {
  key?: string | number | symbol;
  config?: PediatricContactCollectionConfig;
}) => {
  const id = String(element.config?.key ?? element.key ?? 'pediatric-contact');
  return {
    email: `email:${id}`,
    notifyForParticipantsActivities: `notifyForParticipantsActivities:${id}`
  };
};

const CheckboxRow = styled.View<{ disabled?: boolean }>`
  flex-direction: row;
  opacity: ${p => (p.disabled ? 0.6 : 1)};
  align-items: center;
`;

const Checkbox = styled.TouchableOpacity<{
  touched?: boolean;
}>`
  align-self: flex-start;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background-color: ${p =>
    p.touched ? Colors.checkboxTouched : Colors.checkbox};
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

const CaptionContainer = styled.View`
  flex-shrink: 1;
`;

const ErrorText = styled.Text`
  color: ${Colors.error};
  font-family: 'DMSans-Medium';
  font-size: 12px;
  margin: -8px 4px 12px 4px;
`;

const StyledCaption = styled(Caption)`
  margin-right: 8px;
  font-size: ${Platform.OS === 'web' ? 16 : 13}px;
`;

const FieldLabel = styled(Caption)`
  font-size: ${Platform.OS === 'web' ? 14 : 12}px;
  text-transform: uppercase;
  margin-top: 12px;
  margin-bottom: 4px;
`;

const PediatricContactCollection: React.FC<
  FormElementProps<any, PediatricContactCollectionConfig>
> = props => {
  const { element, disabled } = props;
  const {
    formValues,
    setFormValue,
    studyParticipantId,
    dataSourceContext,
    setDisableEventTransition
  } = useDynamicFormContext<any, PediatricContactCollectionConfig>();
  const { studyParticipants } = useUserContext();
  const participant = studyParticipants?.find(
    item => item.id === studyParticipantId
  );
  const study = firstStudyWithPediatricFlag(
    dataSourceContext?.study,
    (formValues as any).study,
    participant?.study
  );
  const configured = element.config?.contactCollection || 'Both';
  const showEmail = allowsEmail(configured);
  const showSms = allowsSms(configured);
  const requireBoth = element.required && configured !== 'Either';
  element.dataSource = parentDataSource(element);
  const keys = instanceKeys(element);
  const emailValue = formValues[keys.email];
  const notifyValue = formValues[keys.notifyForParticipantsActivities];
  const initialEmailRef = useRef(formValues.email);

  const [phoneUsable, setPhoneUsable] = useState(true);
  useEffect(() => {
    setDisableEventTransition(!phoneUsable);
    return () => setDisableEventTransition(false);
  }, [phoneUsable, setDisableEventTransition]);

  useEffect(() => {
    if (!study?.isPediatric || (!showEmail && !showSms)) return;
    const child = participant?.user;
    if (showEmail && emailValue === undefined) {
      const seed = formValues.email ?? initialEmailRef.current ?? child?.email;
      if (seed !== undefined) setFormValue(keys.email, seed);
    }
    if (showEmail && notifyValue === undefined) {
      setFormValue(
        keys.notifyForParticipantsActivities,
        formValues.notifyForParticipantsActivities ?? true
      );
    }
    if (!child) return;
    if (
      showSms &&
      formValues.phoneNumber === undefined &&
      child.phoneNumber !== undefined
    ) {
      setFormValue('phoneNumber', child.phoneNumber);
    }
    if (
      showSms &&
      formValues.phoneNumberCode === undefined &&
      child.phoneNumberCode !== undefined
    ) {
      setFormValue('phoneNumberCode', child.phoneNumberCode);
    }
    if (
      showSms &&
      formValues.notifyForStudy === undefined &&
      child.notifyForStudy !== undefined
    ) {
      setFormValue('notifyForStudy', child.notifyForStudy);
    }
  }, [
    emailValue,
    formValues,
    keys.email,
    keys.notifyForParticipantsActivities,
    notifyValue,
    participant?.user,
    setFormValue,
    showEmail,
    showSms,
    study?.isPediatric
  ]);

  const savedEmailRef = useRef<unknown>(undefined);
  const savedNotifyRef = useRef<unknown>(undefined);
  useEffect(() => {
    if (!showEmail) return;
    if (emailValue !== undefined && emailValue !== savedEmailRef.current) {
      savedEmailRef.current = emailValue;
      setFormValue('email', emailValue);
    }
    if (notifyValue !== undefined && notifyValue !== savedNotifyRef.current) {
      savedNotifyRef.current = notifyValue;
      setFormValue('notifyForParticipantsActivities', notifyValue);
    }
  }, [emailValue, notifyValue, setFormValue, showEmail]);

  if (!study?.isPediatric || (!showEmail && !showSms)) {
    return null;
  }

  const emailNotifications = Boolean(notifyValue);
  const textNotifications = Boolean(formValues.notifyForStudy);
  const hasRequiredNotification =
    !element.required ||
    notificationRequirementMet(
      configured,
      isValidEmail(emailValue),
      emailNotifications,
      formValues.phoneNumber,
      textNotifications
    );
  const showNotificationError =
    !hasRequiredNotification && Boolean(emailValue || formValues.phoneNumber);
  const emailInvalid = Boolean(emailValue) && !isValidEmail(emailValue);
  const emailCheckboxDisabled = disabled || !isValidEmail(emailValue);
  const key = String(element.key || 'pediatric-contact');
  const childProps = {
    stateNode: props.stateNode as any,
    edit: props.edit,
    disabled
  };
  const emailElement = {
    ...element,
    dataSource: keys.email,
    key: `${key}-email`,
    title: '',
    description: '',
    required: requireBoth,
    config: {}
  };
  const phoneElement = {
    ...element,
    dataSource: 'phoneNumber',
    key: `${key}-phone`,
    title: '',
    description: '',
    required: requireBoth,
    config: {}
  };
  const emailNotificationCheckbox = (
    <View>
      <CheckboxRow disabled={emailCheckboxDisabled}>
        <Checkbox
          disabled={emailCheckboxDisabled}
          touched={emailNotifications}
          activeOpacity={0.9}
          onPress={() => {
            if (emailCheckboxDisabled) return;
            setFormValue(
              keys.notifyForParticipantsActivities,
              !emailNotifications
            );
          }}
        >
          {emailNotifications && (
            <Feather name='check' size={14} color='#FFF' />
          )}
        </Checkbox>
        <CaptionContainer>
          <StyledCaption color='dark'>
            Email for study notifications
          </StyledCaption>
        </CaptionContainer>
      </CheckboxRow>
      <Spacer size='small' />
    </View>
  );

  return (
    <View>
      <FormElementQuestion element={element} />
      {showEmail && (
        <>
          <FieldLabel color='dark'>
            {element.config?.emailHeader || DEFAULT_EMAIL_HEADER}
          </FieldLabel>
          <FormTextField {...childProps} element={emailElement} />
          {emailInvalid && <ErrorText>Enter a valid email address.</ErrorText>}
          {!showSms && emailNotificationCheckbox}
        </>
      )}
      {showSms && (
        <>
          <FieldLabel color='dark'>
            {element.config?.phoneHeader || DEFAULT_PHONE_HEADER}
          </FieldLabel>
          <FormPhoneNumber
            {...childProps}
            element={phoneElement}
            showNotifyForMessages={false}
            showNotifyForStudy
            onValidityChange={setPhoneUsable}
          >
            {showEmail && emailNotificationCheckbox}
          </FormPhoneNumber>
        </>
      )}
      {showNotificationError && (
        <ErrorText>
          {configured === 'Email'
            ? 'Email for study notifications must be checked.'
            : configured === 'SMS'
              ? 'Text for study notifications must be checked.'
              : 'At least one study notification method must be checked.'}
        </ErrorText>
      )}
    </View>
  );
};

export const PediatricContactCollectionPlugin: FormElementPlugin<
  any,
  PediatricContactCollectionConfig
> = {
  name: 'Pediatric Contact Collection',
  type: 'PediatricContactCollection',
  element: PediatricContactCollection,
  validate: (element, formValues) => {
    element.dataSource = parentDataSource(element);
    const collection = element.config?.contactCollection || 'Both';
    const keys = instanceKeys(element);
    const email = formValues[keys.email] ?? formValues.email;
    return !(allowsEmail(collection) && email && !isValidEmail(email));
  },
  validateRequired: (element, formValues) => {
    const collection = element.config?.contactCollection || 'Both';
    const keys = instanceKeys(element);
    const email = formValues[keys.email] ?? formValues.email;
    const notifyForEmail =
      formValues[keys.notifyForParticipantsActivities] ??
      formValues.notifyForParticipantsActivities;
    if (allowsEmail(collection) && email && !isValidEmail(email)) return false;
    if (collection !== 'Either') {
      if (allowsEmail(collection) && !isValidEmail(email)) return false;
      if (allowsSms(collection) && !formValues.phoneNumber) return false;
    }
    return notificationRequirementMet(
      collection,
      isValidEmail(email),
      notifyForEmail,
      formValues.phoneNumber,
      formValues.notifyForStudy
    );
  }
};
